import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { useAppStore } from "@/lib/store";
import { Header } from "@/components/Header";
import { BottomNav } from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  User,
  Mail,
  Phone,
  Calendar,
  LogOut,
  Trash2,
  AlertCircle,
  Wallet,
  TrendingUp,
  CheckCircle2,
  ArrowDownToLine,
  Upload,
  X,
  Loader2,
} from "lucide-react";
import { authGuard } from "@/lib/authGuard";
import { toast } from "sonner";
import { changePassword, deleteUserAccount, uploadAvatar, deleteAvatar } from "@/lib/api";
import { Eye, EyeOff } from "lucide-react";

export const Route = createFileRoute("/profile")({
  head: () => ({
    meta: [
      { title: "Profile — PataTasks" },
      {
        name: "description",
        content:
          "Manage your PataTasks tester profile, reset password, and update account details.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: async () => {
    await authGuard();
  },
  component: ProfilePage,
});

function ProfilePage() {
  const { user, logout, account, transactions, authInitialized } = useAppStore();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [deleteWord, setDeleteWord] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [avatarImgError, setAvatarImgError] = useState(false);

  const memberSinceDate = useMemo(() => {
    if (user?.isAdmin === 1 && transactions && transactions.length > 0) {
      const withdrawals = transactions
        .filter((t) => t.type === "withdrawal")
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      if (withdrawals.length > 0) {
        return new Date(withdrawals[0].date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      }
    }
    return new Date(user?.createdAt ?? Date.now()).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [user, transactions]);

  const emailDisplay = user?.email ?? "Not provided";
  const phoneDisplay = user?.phone ?? "Not provided";

  const stats = useMemo(() => {
    const tx = transactions || [];
    const rawBalance = account?.balance ?? 0;
    const totalWithdrawn = tx
      .filter((t) => t.type === "withdrawal")
      .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
    const completedTests = tx.filter((t) => t.type === "app_test" && t.status === "success").length;
    const withdrawalCount = tx.filter((t) => t.type === "withdrawal").length;
    const totalEarnings = rawBalance + totalWithdrawn;
    return {
      totalEarnings,
      completedTests,
      totalWithdrawn,
      withdrawalCount,
    };
  }, [transactions, account]);

  const balanceUsd = (account?.balance ?? 0) / 100;

  const handleLogout = () => {
    logout();
    navigate({ to: "/" });
  };

  const handleDeleteAccount = async () => {
    if (deleteWord !== "DELETE") return;
    setIsDeleting(true);
    try {
      await deleteUserAccount(deleteWord);
      toast.success("Account deleted");
      logout();
      navigate({ to: "/" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete account");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-linear-to-br from-slate-50 via-blue-50/20 to-background pb-20 md:pb-0">
      <div className="mx-auto max-w-7xl px-4 py-6">
        <div className="mb-8 rounded-3xl bg-linear-to-br from-primary to-blue-600 p-6 text-white shadow-xl">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm text-2xl font-bold overflow-hidden">
              {user.avatarFileId && !avatarImgError ? (
                <img
                  src={`/api/avatar/${encodeURIComponent(user.avatarFileId)}`}
                  alt={user.fullName}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  onError={() => setAvatarImgError(true)}
                />
              ) : (
                user.fullName.charAt(0).toUpperCase()
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold">{user.fullName}</h1>
              <p className="text-blue-100 text-sm">Member since {memberSinceDate}</p>
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <div className="rounded-2xl border border-blue-100 bg-linear-to-br from-blue-50 to-blue-25 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="h-4 w-4 text-blue-600" />
              <p className="text-xs text-blue-600 uppercase font-semibold tracking-wide">Balance</p>
            </div>
            <p className="text-2xl font-bold tabular-nums text-foreground">
              ${balanceUsd.toFixed(2)}
            </p>
          </div>
          <div className="rounded-2xl border border-emerald-100 bg-linear-to-br from-emerald-50 to-emerald-25 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              <p className="text-xs text-emerald-600 uppercase font-semibold tracking-wide">
                Total Earnings
              </p>
            </div>
            <p className="text-2xl font-bold tabular-nums text-foreground">
              ${(stats.totalEarnings / 100).toFixed(2)}
            </p>
          </div>
          <div className="rounded-2xl border border-purple-100 bg-linear-to-br from-purple-50 to-purple-25 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-4 w-4 text-purple-600" />
              <p className="text-xs text-purple-600 uppercase font-semibold tracking-wide">
                Tests Completed
              </p>
            </div>
            <p className="text-2xl font-bold tabular-nums text-foreground">
              {stats.completedTests}
            </p>
          </div>
          <div className="rounded-2xl border border-orange-100 bg-linear-to-br from-orange-50 to-orange-25 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <ArrowDownToLine className="h-4 w-4 text-orange-600" />
              <p className="text-xs text-orange-600 uppercase font-semibold tracking-wide">
                Withdrawals
              </p>
            </div>
            <p className="text-2xl font-bold tabular-nums text-foreground">
              {stats.withdrawalCount}
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-blue-200 bg-white p-6 shadow-sm mb-6">
          <h2 className="mb-4 text-sm font-semibold text-slate-500 uppercase tracking-wide">
            Profile Picture
          </h2>
          <div className="flex items-center gap-4">
            <div className="relative h-16 w-16">
              {avatarPreview && !avatarImgError ? (
                <img
                  src={avatarPreview}
                  alt="Profile"
                  className="h-16 w-16 rounded-2xl object-cover border-2 border-blue-200"
                  onError={() => setAvatarImgError(true)}
                />
              ) : user?.avatarFileId && !avatarImgError ? (
                <img
                  src={`/api/avatar/${user?.avatarFileId}`}
                  alt="Profile"
                  className="h-16 w-16 rounded-2xl object-cover border-2 border-blue-200"
                  onError={() => setAvatarImgError(true)}
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-linear-to-br from-blue-700 to-indigo-700 text-xl font-bold text-white">
                  {user.fullName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="flex-1">
              <label className="mb-2 block">
                <span className="inline-flex items-center gap-2 rounded-xl bg-blue-600 hover:bg-green-600 text-white px-3 py-1.5 text-xs font-medium cursor-pointer transition-colors">
                  {isUploading ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Upload className="h-3 w-3" />
                  )}
                  {isUploading ? "Uploading..." : "Upload Photo"}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 2 * 1024 * 1024) {
                        toast.error("File too large. Maximum 2 MB.");
                        return;
                      }
                      setAvatarFile(file);
                      setAvatarPreview(URL.createObjectURL(file));
                      setIsUploading(true);
                      try {
                        await uploadAvatar(file);
                        toast.success("Profile picture updated");
                      } catch (err) {
                        toast.error(err instanceof Error ? err.message : "Upload failed");
                        setAvatarPreview(null);
                      } finally {
                        setIsUploading(false);
                      }
                    }}
                  />
                </span>
              </label>
              {avatarFile && (
                <button
                  onClick={() => {
                    setAvatarFile(null);
                    setAvatarPreview(null);
                  }}
                  className="mt-1 text-xs text-red-600 hover:underline"
                >
                  Cancel
                </button>
              )}
              <p className="mt-1 text-xs text-slate-500">PNG, JPEG, WebP, or GIF. Max 2 MB.</p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-blue-200 bg-white p-6 shadow-sm mb-6">
          <h2 className="mb-4 text-sm font-semibold text-slate-500 uppercase tracking-wide">
            Account Details
          </h2>
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <User className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Full Name</p>
                <p className="text-sm font-medium text-foreground">{user.fullName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Email</p>
                <p className="text-sm font-medium text-foreground">{emailDisplay}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Phone</p>
                <p className="text-sm font-medium text-foreground">{phoneDisplay}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
                <Calendar className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Member Since</p>
                <p className="text-sm font-medium text-foreground">{memberSinceDate}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-blue-200 bg-white p-6 shadow-sm mb-6">
          <h2 className="mb-4 text-sm font-semibold text-slate-500 uppercase tracking-wide">
            Change Password
          </h2>
          <div className="mb-3">
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showPwd ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full rounded-xl border border-border bg-slate-50 px-4 py-2"
                placeholder="Current password"
              />
              <button
                type="button"
                onClick={() => setShowPwd((s) => !s)}
                className="absolute right-2 top-2 text-slate-400"
              >
                {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-medium text-slate-700">New Password</label>
            <input
              type={showPwd ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full rounded-xl border border-border bg-slate-50 px-4 py-2"
              placeholder="New password"
            />
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={async () => {
                setIsChanging(true);
                try {
                  if (!currentPassword || !newPassword) {
                    toast.error("Please fill both fields");
                    return;
                  }
                  await changePassword(currentPassword, newPassword);
                  toast.success("Password changed");
                  setCurrentPassword("");
                  setNewPassword("");
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : "Failed to change password");
                } finally {
                  setIsChanging(false);
                }
              }}
              disabled={isChanging}
              className="flex-1"
            >
              {isChanging ? "Saving..." : "Change Password"}
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border border-blue-200 bg-white p-6 shadow-sm mb-6">
          <h2 className="mb-4 text-sm font-semibold text-slate-500 uppercase tracking-wide">
            Account
          </h2>
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              onClick={handleLogout}
              className="gap-2 rounded-xl text-slate-700 hover:text-slate-900"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
              className="gap-2 rounded-xl text-destructive hover:text-destructive hover:bg-red-50 border-red-200"
            >
              <Trash2 className="h-4 w-4" />
              Delete Account
            </Button>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-60 flex items-center justify-center bg-foreground/40 p-4">
            <div className="w-full max-w-sm rounded-2xl border border-red-200 bg-white p-6 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
                <h2 className="text-lg font-bold text-foreground">Delete Account?</h2>
              </div>
              <p className="mb-4 text-sm text-slate-600">
                This action cannot be undone. All your data, balance, and transaction history will
                be permanently deleted. Any remaining balance will be lost.
              </p>
              <div className="mb-4">
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Enter the word DELETE to confirm
                </label>
                <input
                  type="text"
                  value={deleteWord}
                  onChange={(e) => setDeleteWord(e.target.value)}
                  className="w-full rounded-xl border border-border bg-slate-50 px-4 py-2"
                  placeholder="DELETE"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteWord("");
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className="flex-1"
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </Button>
              </div>
            </div>
          </div>
        )}

        <BottomNav />
      </div>
    </div>
  );
}
